import React, { useCallback, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import {
  BackgroundJobStatusValue,
  isActiveBackgroundJobStatus,
} from 'src/fetch/backgroundJobs/backgroundJobStatus'
import { Button, HStack, Icon, Tooltip, T, VStack } from 'src/govwell-ui'
import { useIsGov } from 'src/layouts/AppStateContextLayout/utils'

import { backgroundJobBarVariantClassName, getJobBarContent } from './backgroundJobMessages'
import { useBackgroundJobs } from './BackgroundJobsProvider'
import { TrackedBackgroundJob } from './types'

type Props = {
  /** Inline in TopBar vs fixed beside the floating AI Assistant pill. */
  placement?: 'inline' | 'floating'
}

const JobStatusIcon = React.memo(({ job }: { job: TrackedBackgroundJob }) => {
  if (isActiveBackgroundJobStatus(job.status)) {
    return <Icon.FaSpinner className="size-3 shrink-0 animate-spin" />
  }
  if (job.status === BackgroundJobStatusValue.Completed) {
    return <Icon.FaCheck className="size-3 shrink-0" />
  }
  if (job.status === BackgroundJobStatusValue.PartiallyCompleted) {
    return <Icon.FaCircleExclamation className="size-3 shrink-0" />
  }
  if (job.status === BackgroundJobStatusValue.Failed) {
    return <Icon.FaCircleXmark className="size-3 shrink-0" />
  }
  if (job.status === BackgroundJobStatusValue.Cancelled) {
    return <Icon.FaBan className="size-3 shrink-0" />
  }

  return null
})
JobStatusIcon.displayName = 'JobStatusIcon'

/** Progress chrome only in 1.2 — pill navigation and Failed/Partial dismiss ship in follow-up PRs. */
const BackgroundJobTopBarStatus = ({ placement = 'inline' }: Props) => {
  const isGov = useIsGov()
  const { notification } = useNotification()
  const { trackedJobs, cancelJob } = useBackgroundJobs()

  const job = useMemo(() => trackedJobs[0], [trackedJobs])

  const handleCancel = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()
      if (!job) {
        return
      }
      try {
        await cancelJob(job.id)
      } catch (error) {
        console.error(error)
        notification.error({ message: 'Could not cancel background job. Please try again.' })
      }
    },
    [cancelJob, job, notification]
  )

  if (!isGov || !job) {
    return null
  }

  const content = getJobBarContent(job)
  const isInProgress = isActiveBackgroundJobStatus(job.status)

  const pillContent = (
    <HStack
      align="center"
      gap={2}
      className={twMerge(
        'min-h-7 max-w-[400px] min-w-0 shrink rounded-full border px-2.5 py-1 text-[11px] font-medium',
        backgroundJobBarVariantClassName(content.variant),
        placement === 'floating' && 'shadow-sm'
      )}
    >
      <JobStatusIcon job={job} />
      <VStack align="stretch" gap={0} className="min-w-0 flex-1">
        <T.Body className="truncate text-inherit">{content.message}</T.Body>
        {content.description && (
          <T.BodySmall className="truncate text-inherit opacity-80">
            {content.description}
          </T.BodySmall>
        )}
      </VStack>
      {isInProgress && (
        <Tooltip content="Cancel">
          <span>
            <Button
              variant="text"
              size="sm"
              icon="FaBan"
              ariaLabel="Cancel background job"
              onClick={handleCancel}
              className="!size-6 shrink-0 !p-0 text-inherit hover:!bg-black/5"
            />
          </span>
        </Tooltip>
      )}
    </HStack>
  )

  const pill = <div>{pillContent}</div>

  if (placement === 'floating') {
    return <div className="pointer-events-auto max-w-[min(400px,calc(100vw-180px))]">{pill}</div>
  }

  return pill
}

export default React.memo(BackgroundJobTopBarStatus)
